import type { SuiteContextV1, SuiteOrganizationV1, SuiteUserV1 } from "@fulcrum5/suite-contracts";
import { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { SuiteOrganizationMark } from "./SuiteOrganizationMark.tsx";
import { useAnchoredPopover } from "./useAnchoredPopover.ts";

export interface SuiteAccountMenuProps {
  readonly context: SuiteContextV1;
  readonly onLogout: () => Promise<void>;
  readonly busy?: boolean;
  readonly className?: string;
}

const ROLE_LABELS: Readonly<Record<SuiteOrganizationV1["role"], string>> = {
  owner: "Owner",
  admin: "Administrator",
  member: "Member",
};

function joinClassNames(...classNames: Array<string | undefined>): string {
  return classNames.filter((className): className is string => Boolean(className)).join(" ");
}

function initialsFor(displayName: string): string {
  const nameParts = displayName.trim().split(/\s+/).filter(Boolean);
  const firstPart = Array.from(nameParts[0] ?? "U");
  const secondPart = Array.from(nameParts[1] ?? "");
  const first = firstPart[0] ?? "U";
  const second = secondPart[0] ?? firstPart[1] ?? first;
  return `${first}${second}`.toLocaleUpperCase();
}

function AccountAvatar({ user }: { readonly user: SuiteUserV1 }): JSX.Element {
  if (user.avatarUrl !== null) {
    return (
      <img
        alt={`${user.displayName} avatar`}
        className="f5-suite-account-menu__avatar-image"
        src={user.avatarUrl}
      />
    );
  }

  return <span className="f5-suite-account-menu__avatar-initials">{initialsFor(user.displayName)}</span>;
}

/**
 * Keeps global session termination behind the host's same-origin logout
 * callback. The menu itself never marks a browser session as signed out.
 */
export function SuiteAccountMenu({
  context,
  onLogout,
  busy = false,
  className,
}: SuiteAccountMenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const isBusy = busy || loggingOut;

  const closeMenu = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);
  const { style: popoverStyle } = useAnchoredPopover({
    open,
    anchorRef: triggerRef,
    panelRef,
    onRequestClose: closeMenu,
  });

  const toggleMenu = useCallback(() => {
    if (isBusy) return;
    if (open) {
      closeMenu();
      return;
    }
    setLogoutError(null);
    setOpen(true);
  }, [closeMenu, isBusy, open]);

  const logout = useCallback(async () => {
    if (isBusy) return;
    setLogoutError(null);
    setLoggingOut(true);
    try {
      await onLogout();
    } catch {
      setLoggingOut(false);
      setLogoutError("Sign out could not be completed. Please try again.");
      return;
    }
    setLoggingOut(false);
  }, [isBusy, onLogout]);

  const activeOrganization = context.activeOrganization;
  const panel = open && typeof document !== "undefined"
    ? createPortal(
      <section
        ref={panelRef}
        aria-busy={isBusy}
        aria-label="Account menu"
        className="f5-suite-root f5-suite-account-menu__panel"
        role="dialog"
        style={popoverStyle}
      >
        <div className="f5-suite-account-menu__identity">
          <span className="f5-suite-account-menu__avatar" aria-hidden="true">
            <AccountAvatar user={context.user} />
          </span>
          <div className="f5-suite-account-menu__details">
            <span className="f5-suite-account-menu__display-name">{context.user.displayName}</span>
            <span className="f5-suite-account-menu__email">{context.user.email ?? "Email unavailable"}</span>
            <span className="f5-suite-account-menu__organization">
              {activeOrganization?.name ?? "Platform administration"}
            </span>
            {activeOrganization !== null && (
              <span className="f5-suite-account-menu__role">{ROLE_LABELS[activeOrganization.role]}</span>
            )}
          </div>
        </div>
        {logoutError !== null && (
          <p className="f5-suite-account-menu__error" role="alert">{logoutError}</p>
        )}
        <button
          type="button"
          className="f5-suite-account-menu__logout"
          disabled={isBusy}
          onClick={() => void logout()}
        >
          Sign out
        </button>
      </section>,
      document.body,
    )
    : null;

  return (
    <div className={joinClassNames("f5-suite-root", "f5-suite-account-menu", className)} data-suite-account="">
      <SuiteOrganizationMark
        className="f5-suite-account-menu__chrome-organization"
        organization={activeOrganization}
      />
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Account menu"
        className="f5-suite-account-menu__trigger"
        disabled={isBusy}
        onClick={toggleMenu}
        title="Account menu"
      >
        <AccountAvatar user={context.user} />
      </button>
      {panel}
    </div>
  );
}
